import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { CertificateStatusEnum } from '@share/common-types';
import { fetchCertificatesWallet } from '@share/store/slices';
import { FunctionalRootState } from '@share/utils';

interface UseCertificatesWalletParams {
  email?: string;
  organizationId?: number;
  statuses?: CertificateStatusEnum[];
  autoFetch?: boolean;
}

interface UseCertificatesWalletResult {
  certificates: FunctionalRootState['certificatesWalletStore']['data'];
  loading: boolean;
  error: string;
  fetchCertificates: () => void;
}

export const useCertificatesWallet = (
  params: UseCertificatesWalletParams = {},
): UseCertificatesWalletResult => {
  const { email, organizationId, statuses, autoFetch = true } = params;
  const dispatch = useDispatch();
  const certificatesWalletStore = useSelector(
    (state: FunctionalRootState) => state.certificatesWalletStore,
  );

  const fetchCertificates = useCallback((): void => {
    dispatch(fetchCertificatesWallet({ email, organizationId, statuses }));
  }, [dispatch, email, organizationId, statuses]);

  useEffect(() => {
    if (!autoFetch) {
      return;
    }

    fetchCertificates();
  }, [autoFetch, fetchCertificates]);

  return {
    certificates: certificatesWalletStore.data,
    loading: certificatesWalletStore.loading,
    error: certificatesWalletStore.error,
    fetchCertificates,
  };
};
